/**
 * 矩阵站站点配置（真理源）
 * -----------------------------------------------------------------------------
 * 目标：通过 host（域名）精确定位到唯一的站点配置，给页面提供 catId / siteName 等。
 *
 * 数据来源：与仓库根目录的 `packages/pc/pc_sites_config.json`、`packages/m/m_sites_config.json`
 *          保持一致（同一份站点清单，这里是代码侧的镜像，供前端运行时使用）。
 *
 * 使用方式：
 *   import { resolveCategoryByHost } from '@share/common/siteConfig'
 *   // 当前平台（pc / m）由构建时注入的环境变量 VITE_PLATFORM 自动识别，
 *   // 无需调用方显式传递。
 *   const category = resolveCategoryByHost(pageContext?.hostname)
 *
 * 特殊场景：如需强制指定平台（例如在 PC 包里测试 M 站点解析），可手动传入第二参数覆盖。
 *
 * 维护规则：
 *   1. 新增 / 删除域名时，三处配置（pc_sites_config.json / m_sites_config.json / 本文件）
 *      必须同步修改；
 *   2. `catId` 必须与 `@share/common/matrix` 中的 `categoryList` 对应项一致，否则页面
 *      无法解析到 ICategory。
 * -----------------------------------------------------------------------------
 */
import {
  ICategory,
  categoryList,
  getCategoryByCatId,
  getCategoryByNav,
} from './matrix'
/** 站点平台 */
export type ISitePlatform = 'pc' | 'm'


/**
 * 获取当前构建/运行中的平台，来源于环境变量 `VITE_PLATFORM`。
 *
 * 取值约定（与仓库 .env.* 保持一致）：
 *   - `'PC'`  -> 返回 'pc'
 *   - `'M'`   -> 返回 'm'
 *   - 其他/缺失 -> 兜底返回 'm'（保证调用方在站点解析失败时仍有可用平台）。
 *
 * 说明：`import.meta.env.VITE_PLATFORM` 在 SSR 和客户端构建时均由 Vite 内联注入，
 *       因此无需运行时读取 process.env。
 */
export const getCurrentPlatform = (): ISitePlatform => {
  const v = String((import.meta.env.VITE_PLATFORM as string) || '')
    .trim()
    .toLowerCase()
  return v === 'pc' ? 'pc' : 'm'
}

/** 站点清单中的单项配置（与 *_sites_config.json 保持同结构） */
export interface ISiteConfig {
  /** 站点展示名，例如：玄幻小说网 */
  siteName: string
  /** 站点logo, 使用 svg, 121 * 44 尺寸 */
  logo: string
  /** 访问域名（不含协议），例如：www.chatyuewen.cn / m.chatyuewen.cn */
  host: string
  /** 站点英文 key，与 matrix.ts 的 ICategory.key / nav 对应 */
  site: string
  /** 平台：pc / m */
  platform: ISitePlatform
  /** 受众性别：male / female */
  sex: 'male' | 'female'
  /** 接口参数：二级分类 ID（字符串形式，与 JSON 保持一致） */
  catId: string
  /** 分类中文名，例如：玄幻 */
  catName: string
  /** Cookie 作用域，例如：.chatyuewen.cn */
  cookieDomain: string
  /** 阅文分成标识（业务自定义，保留字段） */
  readershare: string
  /** 百度统计 hmId（保留字段） */
  hmId: string
  /** 校验类配置（保留字段） */
  verify: Record<string, unknown>
  /**
   * 百度统计 hm.js code（站点级），来源：*_sites_config.json
   * 形如 32 位 16 进制字符串；为空时不渲染百度统计脚本。
   */
  baiduHMCode?: string
  /**
   * 各搜索引擎站长平台的站点验证码（站点级）
   * 目前仅消费 `baidu`，其他字段保留以便扩展。
   */
  siteVerification?: Partial<
    Record<
      'baidu' | '360' | 'shenma' | 'google' | 'sogou' | 'bytedance',
      string
    >
  >
  /**
   * 互联网 ICP 备案号（站点级，对应工信部 beian.miit.gov.cn 的备案）。
   * 矩阵站每个二级分类对应独立顶级域名，备案号各不相同，
   * 形如 `沪B2-20080046-NNN`。空值时 footer 不渲染备案信息块。
   */
  icpFiling?: string
}

// ---------------------------------------------------------------------------
// PC 站点清单（对应 packages/pc/pc_sites_config.json）
// ---------------------------------------------------------------------------
export const PC_SITES: ISiteConfig[] = [
  {
    siteName: '玄幻小说网',
    logo: 'https://imgservices-1252317822.image.myqcloud.com/coco/s05292026/a54220ca.adcmq9.svg',
    host: 'localhost',
    site: 'xuanhuan',
    platform: 'pc',
    sex: 'male',
    catId: '21',
    catName: '玄幻',
    cookieDomain: 'localhost',
    readershare: '',
    hmId: '',
    verify: {},
    baiduHMCode: '52cc2f266507b23c232854f9e1d6e864',
    siteVerification: { baidu: 'codeva-FkFCffxgRn' },
    icpFiling: '沪B2-20080046-119',
  },
  {
    siteName: '玄幻小说网',
    logo: 'https://imgservices-1252317822.image.myqcloud.com/coco/s05292026/a54220ca.adcmq9.svg',
    host: 'www.chatyuewen.cn',
    site: 'xuanhuan',
    platform: 'pc',
    sex: 'male',
    catId: '21',
    catName: '玄幻',
    cookieDomain: '.chatyuewen.cn',
    readershare: '',
    hmId: '',
    verify: {},
    baiduHMCode: '52cc2f266507b23c232854f9e1d6e864',
    siteVerification: { baidu: 'codeva-FkFCffxgRn' },
    icpFiling: '沪B2-20080046-119',
  },
  {
    siteName: '奇幻小说网',
    logo: 'https://imgservices-1252317822.image.myqcloud.com/coco/s05292026/969f9e09.oqx6n4.svg',
    host: 'www.chatqidian.cn',
    site: 'qihuan',
    platform: 'pc',
    sex: 'male',
    catId: '1',
    catName: '奇幻',
    cookieDomain: '.chatqidian.cn',
    readershare: '',
    hmId: '',
    verify: {},
    baiduHMCode: 'ae66986bb62fe6b9aa5c917bbf5eca33',
    siteVerification: { baidu: 'codeva-S8zAgwrGOS' },
    icpFiling: '沪B2-20080046-114',
  },
  {
    siteName: '武侠小说网',
    logo: 'https://imgservices-1252317822.image.myqcloud.com/coco/s05292026/d7435081.4e5a4n.svg',
    host: 'www.chatqidian.net',
    site: 'wuxia',
    platform: 'pc',
    sex: 'male',
    catId: '2',
    catName: '武侠',
    cookieDomain: '.chatqidian.net',
    readershare: '',
    hmId: '',
    verify: {},
    baiduHMCode: 'd5a3a5ffe173d1900eb0d97e2a1abf24',
    siteVerification: { baidu: 'codeva-y1RmIJq7uF' },
    icpFiling: '沪B2-20080046-116',
  },
  {
    siteName: '仙侠小说网',
    logo: 'https://imgservices-1252317822.image.myqcloud.com/coco/s05292026/45102cc3.2byj69.svg',
    host: 'www.chatdrama.cn',
    site: 'xianxia',
    platform: 'pc',
    sex: 'male',
    catId: '22',
    catName: '仙侠',
    cookieDomain: '.chatdrama.cn',
    readershare: '',
    hmId: '',
    verify: {},
    baiduHMCode: '393da33d48af0ea77ba856d440e41f74',
    siteVerification: { baidu: 'codeva-t7CQFw08cy' },
    icpFiling: '沪B2-20080046-121',
  },
  {
    siteName: '都市小说网',
    logo: 'https://imgservices-1252317822.image.myqcloud.com/coco/s05292026/4be362a7.mc2kb4.svg',
    host: 'www.chatwrite.net',
    site: 'dushi',
    platform: 'pc',
    sex: 'male',
    catId: '4',
    catName: '都市',
    cookieDomain: '.chatwrite.net',
    readershare: '',
    hmId: '',
    verify: {},
    baiduHMCode: '3b5edb307ca2bb305607e3d6e5378992',
    siteVerification: { baidu: 'codeva-GfAFyFQhUD' },
    icpFiling: '沪B2-20080046-118',
  },
  {
    siteName: '历史小说网',
    logo: 'https://imgservices-1252317822.image.myqcloud.com/coco/s05292026/0e189be7.9348pu.svg',
    host: 'www.chatdrama.net',
    site: 'lishi',
    platform: 'pc',
    sex: 'male',
    catId: '5',
    catName: '历史',
    cookieDomain: '.chatdrama.net',
    readershare: '',
    hmId: '',
    verify: {},
    baiduHMCode: '34ff0e026238f6ac0ba9d043313618a8',
    siteVerification: { baidu: 'codeva-jaqvNCDQbo' },
    icpFiling: '沪B2-20080046-115',
  },
  {
    siteName: '游戏小说网',
    logo: 'https://imgservices-1252317822.image.myqcloud.com/coco/s05292026/26c2638a.e1l3nh.svg',
    host: 'www.chatxiaoxiang.com',
    site: 'youxi',
    platform: 'pc',
    sex: 'male',
    catId: '7',
    catName: '游戏',
    cookieDomain: '.chatxiaoxiang.com',
    readershare: '',
    hmId: '',
    verify: {},
    baiduHMCode: 'ca7904d1fce24fb159ee1370bbd127c6',
    siteVerification: { baidu: '' },
    icpFiling: '沪B2-20080046-120',
  },
  {
    siteName: '科幻小说网',
    logo: 'https://imgservices-1252317822.image.myqcloud.com/coco/s05292026/b4eaa42e.b96lgn.svg',
    host: 'www.chattext.cn',
    site: 'kehuan',
    platform: 'pc',
    sex: 'male',
    catId: '9',
    catName: '科幻',
    cookieDomain: '.chattext.cn',
    readershare: '',
    hmId: '',
    verify: {},
    baiduHMCode: 'cef765b36a323cbae3e58e4138c0a2b3',
    siteVerification: { baidu: '' },
    icpFiling: '沪B2-20080046-117',
  },
  {
    siteName: '轻小说网',
    logo: 'https://imgservices-1252317822.image.myqcloud.com/coco/s05292026/651ab978.8s0x7t.svg',
    host: 'www.aireads.cn',
    site: '2cy',
    platform: 'pc',
    sex: 'male',
    catId: '12',
    catName: '轻小说',
    cookieDomain: '.aireads.cn',
    readershare: '',
    hmId: '',
    verify: {},
    baiduHMCode: '7324b6880e7e876d76c9c53e0586c0b9',
    siteVerification: { baidu: '' },
    icpFiling: '沪B2-20080046-112',
  },
  {
    siteName: '诸天无限小说网',
    logo: 'https://imgservices-1252317822.image.myqcloud.com/coco/s05292026/26d56b7c.380bsz.svg',
    host: 'www.dishu3.com',
    site: 'zhutianwuxian',
    platform: 'pc',
    sex: 'male',
    catId: '20109',
    catName: '诸天无限',
    cookieDomain: '.dishu3.com',
    readershare: '',
    hmId: '',
    verify: {},
    baiduHMCode: '2cb75dee24ba4fbd21b10710c65c48a0',
    siteVerification: { baidu: '' },
    icpFiling: '沪B2-20080046-113',
  },
]

// ---------------------------------------------------------------------------
// M 站点清单（对应 packages/m/m_sites_config.json）
// ---------------------------------------------------------------------------
export const M_SITES: ISiteConfig[] = [
  {
    siteName: '玄幻小说网',
    logo: 'https://imgservices-1252317822.image.myqcloud.com/coco/s05292026/a54220ca.adcmq9.svg',
    host: 'localhost',
    site: 'xuanhuan',
    platform: 'm',
    sex: 'male',
    catId: '21',
    catName: '玄幻',
    cookieDomain: 'localhost',
    readershare: '',
    hmId: '',
    verify: {},
    baiduHMCode: '62e96b2ac4a71c4c68aaf524bb0c9704',
    siteVerification: { baidu: 'codeva-C3oZYI2M1e' },
    icpFiling: '沪B2-20080046-119',
  },
  {
    siteName: '玄幻小说网',
    logo: 'https://imgservices-1252317822.image.myqcloud.com/coco/s05292026/a54220ca.adcmq9.svg',
    host: 'm.chatyuewen.cn',
    site: 'xuanhuan',
    platform: 'm',
    sex: 'male',
    catId: '21',
    catName: '玄幻',
    cookieDomain: '.chatyuewen.cn',
    readershare: '',
    hmId: '',
    verify: {},
    baiduHMCode: '62e96b2ac4a71c4c68aaf524bb0c9704',
    siteVerification: { baidu: 'codeva-C3oZYI2M1e' },
    icpFiling: '沪B2-20080046-119',
  },
  {
    siteName: '奇幻小说网',
    logo: 'https://imgservices-1252317822.image.myqcloud.com/coco/s05292026/969f9e09.oqx6n4.svg',
    host: 'm.chatqidian.cn',
    site: 'qihuan',
    platform: 'm',
    sex: 'male',
    catId: '1',
    catName: '奇幻',
    cookieDomain: '.chatqidian.cn',
    readershare: '',
    hmId: '',
    verify: {},
    baiduHMCode: '3c58fec924a1843aec929df8ef2d8955',
    siteVerification: { baidu: 'codeva-qNEF6cqqhz' },
    icpFiling: '沪B2-20080046-114',
  },
  {
    siteName: '武侠小说网',
    logo: 'https://imgservices-1252317822.image.myqcloud.com/coco/s05292026/d7435081.4e5a4n.svg',
    host: 'm.chatqidian.net',
    site: 'wuxia',
    platform: 'm',
    sex: 'male',
    catId: '2',
    catName: '武侠',
    cookieDomain: '.chatqidian.net',
    readershare: '',
    hmId: '',
    verify: {},
    baiduHMCode: 'b3083c51b8eaf112d3acb810f4eb3a84',
    siteVerification: { baidu: 'codeva-VLH78imDXa' },
    icpFiling: '沪B2-20080046-116',
  },
  {
    siteName: '仙侠小说网',
    logo: 'https://imgservices-1252317822.image.myqcloud.com/coco/s05292026/45102cc3.2byj69.svg',
    host: 'm.chatdrama.cn',
    site: 'xianxia',
    platform: 'm',
    sex: 'male',
    catId: '22',
    catName: '仙侠',
    cookieDomain: '.chatdrama.cn',
    readershare: '',
    hmId: '',
    verify: {},
    baiduHMCode: '11a09a387df317ef871f18da23412517',
    siteVerification: { baidu: 'codeva-u8mZPOkDUt' },
    icpFiling: '沪B2-20080046-121',
  },
  {
    siteName: '都市小说网',
    logo: 'https://imgservices-1252317822.image.myqcloud.com/coco/s05292026/4be362a7.mc2kb4.svg',
    host: 'm.chatwrite.net',
    site: 'dushi',
    platform: 'm',
    sex: 'male',
    catId: '4',
    catName: '都市',
    cookieDomain: '.chatwrite.net',
    readershare: '',
    hmId: '',
    verify: {},
    baiduHMCode: '36a01df40a34cc0dd94bb3c030000eab',
    siteVerification: { baidu: '' },
    icpFiling: '沪B2-20080046-118',
  },
  {
    siteName: '历史小说网',
    logo: 'https://imgservices-1252317822.image.myqcloud.com/coco/s05292026/0e189be7.9348pu.svg',
    host: 'm.chatdrama.net',
    site: 'lishi',
    platform: 'm',
    sex: 'male',
    catId: '5',
    catName: '历史',
    cookieDomain: '.chatdrama.net',
    readershare: '',
    hmId: '',
    verify: {},
    baiduHMCode: 'c6090b361c245cdf25e0095f239e7396',
    siteVerification: { baidu: '' },
    icpFiling: '沪B2-20080046-115',
  },
  {
    siteName: '游戏小说网',
    logo: 'https://imgservices-1252317822.image.myqcloud.com/coco/s05292026/26c2638a.e1l3nh.svg',
    host: 'm.chatxiaoxiang.com',
    site: 'youxi',
    platform: 'm',
    sex: 'male',
    catId: '7',
    catName: '游戏',
    cookieDomain: '.chatxiaoxiang.com',
    readershare: '',
    hmId: '',
    verify: {},
    baiduHMCode: 'bbb5f7cb652207ca1bf4a6028c8a67d3',
    siteVerification: { baidu: '' },
    icpFiling: '沪B2-20080046-120',
  },
  {
    siteName: '科幻小说网',
    logo: 'https://imgservices-1252317822.image.myqcloud.com/coco/s05292026/b4eaa42e.b96lgn.svg',
    host: 'm.chattext.cn',
    site: 'kehuan',
    platform: 'm',
    sex: 'male',
    catId: '9',
    catName: '科幻',
    cookieDomain: '.chattext.cn',
    readershare: '',
    hmId: '',
    verify: {},
    baiduHMCode: '1e38f1683047ffdba55be4039333f6dc',
    siteVerification: { baidu: '' },
    icpFiling: '沪B2-20080046-117',
  },
  {
    siteName: '轻小说网',
    logo: 'https://imgservices-1252317822.image.myqcloud.com/coco/s05292026/651ab978.8s0x7t.svg',
    host: 'm.aireads.cn',
    site: '2cy',
    platform: 'm',
    sex: 'male',
    catId: '12',
    catName: '轻小说',
    cookieDomain: '.aireads.cn',
    readershare: '',
    hmId: '',
    verify: {},
    baiduHMCode: 'ee0a8d0ac762c32e37848f176523b55c',
    siteVerification: { baidu: '' },
    icpFiling: '沪B2-20080046-112',
  },
  {
    siteName: '诸天无限小说网',
    logo: 'https://imgservices-1252317822.image.myqcloud.com/coco/s05292026/26d56b7c.380bsz.svg',
    host: 'm.dishu3.com',
    site: 'zhutianwuxian',
    platform: 'm',
    sex: 'male',
    catId: '20109',
    catName: '诸天无限',
    cookieDomain: '.dishu3.com',
    readershare: '',
    hmId: '',
    verify: {},
    baiduHMCode: '5116cead84f56367a87b78aa2caaa518',
    siteVerification: { baidu: '' },
    icpFiling: '沪B2-20080046-113',
  },
]

/** 站点清单合集（PC + M） */
export const ALL_SITES: ISiteConfig[] = [...PC_SITES, ...M_SITES]

// ---------------------------------------------------------------------------
// 工具函数
// ---------------------------------------------------------------------------

// 模块顶层常量正则：避免每次调用重新编译。
const ENV_PREFIX_RE = /^(?:local|oa|pre)((?:m|www)\..+)$/
const SAFE_HOST_CHARSET_RE = /^[a-z0-9.-]+$/
const BAIDU_HM_CODE_RE = /^[0-9a-f]{32}$/i
const BAIDU_VERIFICATION_RE = /^[A-Za-z0-9_-]{1,128}$/
// 互联网 ICP 备案号：固定主体 `沪B2-20080046-` + 数字尾号（1~6 位足够当前规模）。
// 严格校验，防止 hostname 被恶意构造时备案号字符串注入到 footer 输出 HTML。
const ICP_FILING_RE = /^沪B2-20080046-\d{1,6}$/

// host -> ISiteConfig 索引：精确匹配走 O(1)，避免每次请求扫描整个数组。
// 站点清单在模块加载时即定型，不会运行时变更，构建一次即可。
const PC_HOST_INDEX = new Map<string, ISiteConfig>(
  PC_SITES.map((s) => [s.host, s]),
)
const M_HOST_INDEX = new Map<string, ISiteConfig>(
  M_SITES.map((s) => [s.host, s]),
)
const ALL_HOST_INDEX = new Map<string, ISiteConfig>([
  ...PC_HOST_INDEX,
  ...M_HOST_INDEX,
])

/**
 * 剥离 host 中的环境前缀（oa / pre / local 等），便于与 JSON 中的生产域名匹配。
 *
 * 严格规则：仅当 prefix 紧跟 `m.` 或 `www.` 时才剥离，避免误吞合法主机名。
 * 例如：
 *   oawww.chatyuewen.cn      -> www.chatyuewen.cn
 *   prem.chatyuewen.cn       -> m.chatyuewen.cn
 *   localm.chatyuewen.cn     -> m.chatyuewen.cn
 *   www.chatyuewen.cn        -> www.chatyuewen.cn（不变）
 *   localhost                -> localhost（不再被误吞为 host）
 *   prep.example.com         -> prep.example.com（不再被误吞为 p.example.com）
 *   oasis.example.com        -> oasis.example.com（不再被误吞）
 */
export const stripEnvPrefix = (host: string): string => {
  const h = (host || '').toLowerCase().trim()
  if (!h) return ''
  // 必须紧跟 m. 或 www.，否则视为正常 host 不剥离
  const m = ENV_PREFIX_RE.exec(h)
  return m ? m[1] : h
}

/**
 * 规范化 host：剥离环境前缀 + 去除端口；空值返回空串。
 * 内部工具，调用 `findSiteByHost` 等公开 API 时仍允许传入 raw host。
 */
const normalizeHost = (host?: string | null): string => {
  if (!host) return ''
  return stripEnvPrefix(host).split(':')[0]
}

/**
 * 根据 host 精确匹配站点（剔除环境前缀；忽略端口）。
 * @param host 原始 host（可带环境前缀 / 端口）
 * @param platform 平台过滤（可选，不传则按 VITE_PLATFORM 自动匹配）
 */
export const findSiteByHost = (
  host: string,
  platform?: ISitePlatform,
): ISiteConfig | null => {
  const pure = normalizeHost(host)
  if (!pure) return null
  // 未显式传入 platform 时，自动从 VITE_PLATFORM 环境变量读取
  const effectivePlatform = platform ?? getCurrentPlatform()
  const index =
    effectivePlatform === 'pc'
      ? PC_HOST_INDEX
      : effectivePlatform === 'm'
      ? M_HOST_INDEX
      : ALL_HOST_INDEX
  // 1) 完全相等：O(1) 命中
  const exact = index.get(pure)
  if (exact) return exact
  // 2) 严格后缀兜底：要求 pure 以 `.${site.host}` 结尾
  //    避免 `malicious-www.chatyuewen.cn` 这类前缀拼接形式被 endsWith 误命中
  for (const site of index.values()) {
    if (pure.endsWith(`.${site.host}`)) return site
  }
  return null
}

/**
 * 根据 host 解析出对应站点的 cookieDomain（用于设置 / 清除 Cookie）。
 *
 * 矩阵站每个二级分类对应独立顶级域名（如 .chatyuewen.cn），
 * 因此 Cookie 的 domain 不能再像起点单站那样写死 `.qidian.com`，
 * 必须按当前请求 host 动态返回对应的 cookieDomain。
 *
 * 解析规则：
 *   1. 先按 *_sites_config 精确匹配 host -> 取 site.cookieDomain
 *   2. 否则尝试取 host 的最后两段（如 a.b.example.com -> .example.com）
 *      —— 此分支严格校验字符集，防止 SSR 注入恶意字符引发 XSS
 *   3. 兜底：返回空字符串（调用方应判断后再决定是否设置 domain）
 *
 * @param host 原始 host（可带环境前缀 / 端口；浏览器侧可传 location.hostname；
 *             SSR 侧可传 pageContext.hostname；JS 端 fallback 时也可不传）
 */
export const getCookieDomainByHost = (host?: string | null): string => {
  // 浏览器场景下 host 缺省时，使用 location.hostname
  const raw =
    host ||
    (typeof window !== 'undefined' ? window?.location?.hostname : '') ||
    ''
  const pure = normalizeHost(raw)
  if (!pure) return ''

  // 1) 站点清单精确匹配（白名单，最安全的来源）
  const site = findSiteByHost(pure)
  if (site?.cookieDomain) return site.cookieDomain

  // 2) 截取主域名兜底（最后两段）。
  //    安全要求：`pure` 来源是 HTTP Host 头，攻击者可控（CWE-79），
  //    必须严格校验字符集（仅 a-z 0-9 . -），命中非法字符直接返回空。
  if (!SAFE_HOST_CHARSET_RE.test(pure)) return ''
  const parts = pure.split('.').filter(Boolean)
  if (parts.length >= 2) {
    return `.${parts.slice(-2).join('.')}`
  }
  return ''
}

/**
 * 取当前 host 对应站点的「百度统计 hm.js code」。
 * - 命中站点清单时返回 `baiduHMCode`；
 * - 严格白名单校验（精确 32 位 16 进制，与百度统计实际格式一致），
 *   防止 hostname 被恶意构造时 baiduHMCode 串入 `hm.baidu.com/hm.js?...`
 *   的查询参数中（CWE-79 / CWE-918）。
 * - 校验失败 / 未命中 / 字段为空时返回空串，调用方应据此跳过百度统计脚本注入。
 */
export const getBaiduHMCodeByHost = (host?: string | null): string => {
  const code = findSiteByHost(host || '')?.baiduHMCode?.trim() || ''
  return code && BAIDU_HM_CODE_RE.test(code) ? code : ''
}

/**
 * 取当前 host 对应站点的「百度站长验证码」。
 * - 命中站点清单时返回 `siteVerification.baidu`；
 * - 严格白名单校验（仅字母、数字、`-` 与 `_`，最长 128 位），防止注入到
 *   `<meta name="baidu-site-verification" content="...">` 时引入引号 / 标签字符。
 * - 校验失败 / 未命中 / 字段为空时返回空串。
 */
export const getBaiduSiteVerificationByHost = (
  host?: string | null,
): string => {
  const code = findSiteByHost(host || '')?.siteVerification?.baidu?.trim() || ''
  return code && BAIDU_VERIFICATION_RE.test(code) ? code : ''
}

/**
 * 取当前 host 对应站点的「互联网 ICP 备案号」。
 * - 命中站点清单时返回 `icpFiling`，例如 `沪B2-20080046-119`；
 * - 严格白名单校验（固定主体 + 数字尾号），防止 hostname 被恶意构造时
 *   备案号字符串串入 footer 输出 HTML 引发 XSS（CWE-79）；
 * - 校验失败 / 未命中 / 字段为空时返回空串，调用方应据此跳过备案号渲染或回退兜底。
 */
export const getIcpFilingByHost = (host?: string | null): string => {
  const v = findSiteByHost(host || '')?.icpFiling?.trim() || ''
  return v && ICP_FILING_RE.test(v) ? v : ''
}

/**
 * 根据 nav（站点 key）+ platform 生成站点基础域名（协议相对 URL）。
 * -----------------------------------------------------------------
 * 单一数据源：直接从 PC_SITES / M_SITES 派生，不再单独维护 NAV→DOMAIN 映射，
 * 避免与 *_sites_config.json 数据漂移。
 *
 *   生产环境:    `//<host>`
 *   非生产环境:  `//<env><host>`（env 为 oa/pre；local 视为 pre）
 *
 * @param nav 站点 key（与 ISiteConfig.site / ICategory.nav 一致）
 * @param platform 'm' | 'pc'，默认 'm'
 * @returns 形如 `//m.chatyuewen.cn` 或 `//prewww.chatyuewen.cn`；
 *          nav 未命中清单时返回空字符串（调用方自行处理）。
 */
export const getSiteDomain = (
  nav: string,
  platform: ISitePlatform = 'm',
): string => {
  const env = import.meta.env.VITE_ENV_TYPE
  // pro -> ''；local 视为 pre；其余直接使用 env 名作为前缀
  const prefix =
    env === 'pro' ? '' : env === 'local' ? 'pre' : String(env || '')
  const pool = platform === 'pc' ? PC_SITES : M_SITES
  const matched = pool.find((s) => s.site === nav)
  return matched ? `//${prefix}${matched.host}` : ''
}

/**
 * 根据 host 解析出 ICategory（页面渲染 & 接口调用所需）。
 *
 * 解析优先级：
 *   1. 按 *_sites_config（本模块）的 host 精确匹配 -> 用 catId 映射到 ICategory
 *   2. 回退：按 matrix.ts 旧逻辑匹配 host 中的 nav 关键字（兼容本地调试 / 非矩阵站域名）
 *   3. 回退：读取 VITE_SITE_NAV 环境变量
 *   4. 兜底：返回 categoryList[0]（玄幻）
 *
 * @param host pageContext.hostname
 * @param platform 可选。未传时自动从环境变量 `VITE_PLATFORM` 识别；
 *                传入时以入参为准（用于单元测试等特殊场景）。
 */
export const resolveCategoryByHost = (
  host: string | undefined | null,
  platform?: ISitePlatform,
): ICategory => {
  const h = (host || '').toLowerCase()
  const effectivePlatform = platform ?? getCurrentPlatform()

  // 1) JSON 精确匹配
  const site = findSiteByHost(h, effectivePlatform)
  if (site) {
    const cat = getCategoryByCatId(site.catId)
    if (cat) return cat
    // catId 未在 categoryList（可能被注释掉），尝试按 site.site 作为 nav 匹配
    const byNav = getCategoryByNav(site.site)
    if (byNav) return byNav
  }

  // 2) 旧的 nav 关键字匹配（兼容本地调试 / 非矩阵站域名）。
  //    `includes(c.nav)` 已覆盖 `.${nav}.` / `${nav}.` / `www.${nav}.` / `m.${nav}.`
  //    等所有子串场景，原先的多个 includes 分支属于冗余，统一为单次判定。
  for (const c of categoryList) {
    if (h.includes(c.nav)) return c
  }

  // 3) 环境变量兜底
  const envNav = (import.meta.env.VITE_SITE_NAV as string) || ''
  const envMatch = envNav ? getCategoryByNav(envNav) : null
  if (envMatch) return envMatch

  // 4) 最终兜底
  return categoryList[0]
}
